import { useState } from "react";

import { QueryBarGroupItem } from "@similarweb/ui-components";

import type { IKeywordGroup, QueryBarItemMode, ServicesType } from "app/components/compare/KeywordsQueryBar/KeywordsQueryBarTypes";
import { translate } from "app/filters/i18nFilter";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";

import { KEYWORD, KEYWORD_GROUP, SEARCH_TERM, TOPICS_SECONDARY_TEXT } from "../constants";
import { CompareButton, ItemContainer, KeywordContainer } from "../KeywordsQueryBarStyles";

import { AddToGroupButton } from "./AddToGroup";
import { CreateNewGroupButton } from "./CreateNewGroup";
import { EditGroupButton } from "./EditGroup";
import { useTopicText } from "./helpers/useTopicText";
import { OpenLinkButton } from "./OpenLinkButton";

interface KeywordsQueryBarItemProps {
    keyword?: string;
    keywordGroup?: IKeywordGroup;
    mode: QueryBarItemMode;
    secondaryText?: string;
    onItemClick: () => void;
    showButtons: boolean;
    showSearch: boolean;
    showAutoCreateListCTA?: boolean;
    showCompactCreateListCTAButton?: boolean;
    showCompareButton?: boolean;
    showGoToMonitorKeywordListButton?: boolean;
    shouldConfirmDeleteKeywordList?: boolean;
    onCompareButtonClick?: VoidFunction;
    services: ServicesType;
    onSaveKeywordList?: (keywordGroup?: IUserKeywordGroup) => void;
    onEditKeywordList?: (keywordGroup?: IUserKeywordGroup) => void;
}

export const KeywordsQueryBarItem = ({
    keyword,
    keywordGroup,
    mode,
    showButtons,
    onItemClick,
    services,
    secondaryText,
    showSearch,
    showAutoCreateListCTA = true,
    showCompactCreateListCTAButton = true,
    showCompareButton = false,
    showGoToMonitorKeywordListButton = false,
    shouldConfirmDeleteKeywordList = false,
    onCompareButtonClick,
    onSaveKeywordList = (keywordGroup?: IUserKeywordGroup) => undefined,
    onEditKeywordList = (keywordGroup?: IUserKeywordGroup) => undefined,
}: KeywordsQueryBarItemProps) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const googleLink = `https://www.google.com/search?q=${keyword}`;

    const topicText = useTopicText(keyword, mode === "topic");

    const renderKeywordButtons = () => (
        <>
            {showSearch ? <OpenLinkButton googleLink={googleLink} /> : null}
            <AddToGroupButton
                isDropdownOpen={isDropdownOpen}
                keyword={keyword}
                services={services}
                setIsDropdownOpen={setIsDropdownOpen}
            />
            {showAutoCreateListCTA ? (
                <CreateNewGroupButton
                    keyword={keyword}
                    showCompactButton={showCompactCreateListCTAButton}
                />
            ) : null}
        </>
    );

    const renderKeywordGroupButtons = () =>
        keywordGroup?.isShared ? null : (
            <EditGroupButton
                services={services}
                keywordGroup={keywordGroup}
                showGoToMonitorKeywordListButton={showGoToMonitorKeywordListButton}
                shouldConfirmDeleteKeywordList={shouldConfirmDeleteKeywordList}
                onEditKeywordList={onEditKeywordList}
            />
        );

    const { buttons, secondaryTextKey, text, icon } = (() => {
        switch (mode) {
            // In case we selected a keyword - show keyword action buttons
            case "keyword":
                return {
                    buttons: renderKeywordButtons(),
                    secondaryTextKey: KEYWORD,
                    text: keyword,
                    icon: "search",
                };
            case "searchTerm":
                return {
                    buttons: null,
                    secondaryTextKey: SEARCH_TERM,
                    text: keyword,
                    icon: "search-term",
                };
            case "topic":
                return {
                    buttons: null,
                    secondaryTextKey: TOPICS_SECONDARY_TEXT,
                    text: topicText,
                    icon: "trend-up",
                };
            // otherwise - show edit keyword group button, in case
            // where the group is my group. (not a shared group);
            case "keywordGroup":
            case "keywordGroupFull":
                return {
                    buttons: renderKeywordGroupButtons(),
                    secondaryTextKey: KEYWORD_GROUP,
                    text: keywordGroup?.text ?? "",
                    icon: "nav-keyword-group",
                };
            default:
                return {
                    buttons: null,
                    secondaryTextKey: KEYWORD_GROUP,
                    text: keywordGroup?.text ?? "",
                    icon: "nav-keyword-group",
                };
        }
    })();

    return (
        <ItemContainer>
            <KeywordContainer>
                <QueryBarGroupItem
                    text={text}
                    icon={icon}
                    iconName="arrow"
                    iconSize="10px"
                    onItemClick={onItemClick}
                    // Either display secondaryText from props or decide by `mode` prop
                    secondaryText={secondaryText ?? translate(secondaryTextKey)}
                />
            </KeywordContainer>
            {showButtons ? buttons : null}
            {showCompareButton ? (
                <CompareButton
                    className={"QueryBar-compare-button"}
                    onClick={onCompareButtonClick}
                    iconName="add"
                >
                    {services.translate("keyword.query.bar.compare.button.text")}
                </CompareButton>
            ) : null}
        </ItemContainer>
    );
};
